import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import { useAuth } from "@/components/auth-provider";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>) => ({
    next: safeReturnPath(search.next),
  }),
  component: LoginPage,
});

function safeReturnPath(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;

  try {
    const base = "http://ps-admin.local";
    const url = new URL(value, base);
    if (url.origin !== base || url.pathname === "/login") return undefined;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}

function LoginPage() {
  const { user, loading, signingIn, error, signInWithGoogle } = useAuth();
  const { next } = Route.useSearch();
  const router = useRouter();
  const t = useTranslations("Login");

  useEffect(() => {
    if (!loading && user) {
      router.replace(next ?? "/");
    }
  }, [user, loading, next, router]);

  if (loading || user) {
    return null;
  }

  return (
    <div className="flex min-h-svh items-center justify-center">
      <Card className="w-full max-w-sm">
        <CardHeader className="text-center">
          <div className="bg-muted mx-auto mb-4 flex size-12 items-center justify-center rounded-md">
            <img
              src="/logo.png"
              alt="Pronto Sinistro"
              width={28}
              height={28}
              className="size-7"
            />
          </div>
          <CardTitle className="text-2xl">{t("title")}</CardTitle>
          <CardDescription>{t("description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <p className="text-destructive text-center text-sm">
              {error === "unauthorized"
                ? t("unauthorized")
                : error === "network"
                  ? t("networkError")
                  : t("error")}
            </p>
          )}
          <Button
            variant="outline"
            className="w-full"
            onClick={signInWithGoogle}
            disabled={signingIn}
            aria-busy={signingIn}
          >
            <GoogleIcon className="mr-2 size-4" />
            {error === "network" || error === "error"
              ? t("retry")
              : t("signInWithGoogle")}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
